import React, { useMemo } from "react";
import { useCarFilterSelector } from "../../../redux/store";
import FilterDropdown from "./FilterDropdown";
import * as Styles from "./styles/FilterListStyles";
import { getCurrentFilters, isHiddenFilter } from "../../../utils/filters";
import useFilterList from "../../../hooks/filters/useFilterList";
import SidebarFilterWrapper from "./ComponentFilterWrapper";
import ModalFilterWrapper from "./ModalFilterWrapper";

type Props = { isModal: boolean };

const FilterList = ({ isModal }: Props): JSX.Element => {
    const carFilters = useCarFilterSelector((state) => state.carFilters);

    const { currentFilter } = carFilters;

    const filterIds = getCurrentFilters(currentFilter);

    // Calculate and sort the filters that should be rendered.
    // Car Type is always excluded from this list as it is rendered first and has custom render logic.
    const activeFilters = Object.values(filterIds)
        .filter((filterId) => carFilters[filterId].show && !carFilters[filterId].disabled && !isHiddenFilter(filterId))
        .sort((a, b) => carFilters[a].index - carFilters[b].index);

    const { activeFilterIds, dropdownClick, activeFiltersKey } = useFilterList();

    return useMemo(() => {
        return (
            <Styles.Wrapper>
                <Styles.Inner role="list">
                    {activeFilters.map((filterId) => (
                        <FilterDropdown
                            key={filterId}
                            show={activeFilterIds.includes(filterId)}
                            onClick={dropdownClick(filterId)}
                            title={carFilters[filterId].label}
                            filterId={filterId}
                        >
                            {
                                // In retailer screens, there's the ability to overwrite components used in USC
                                // to different components (or just to null). See componentRegistry.tsx
                                // The problem is that the needed component registry provider currently does not
                                // support being wrapped around a modal. Therefore, if opened in a modal,
                                // use the non-component registry version of the filter wrapper.
                                isModal ? (
                                    <ModalFilterWrapper filterId={filterId} />
                                ) : (
                                    <SidebarFilterWrapper filterId={filterId} />
                                )
                            }
                        </FilterDropdown>
                    ))}
                    <Styles.Inner />
                </Styles.Inner>
            </Styles.Wrapper>
        );
    }, [activeFiltersKey, activeFilterIds, currentFilter]);
};

export default FilterList;
